import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { BackButton, Button, FloatPill, Handle, IconTile, InlineNote, MapSheet, SheetFoot, VehicleImage, useNote } from '../components/ui';
import { Icon } from '../components/Icon';
import { MapLine, MapMarker, MapView, useFlyTo } from '../components/MapView';
import { Screen } from '../components/Screen';
import { useApp } from '../lib/app';
import { rupees } from '../lib/format';
import { haversine, samePlace } from '../lib/geo';
import { finishLater } from '../lib/live';
import { nearestLandmark } from '../lib/places';
import { addressOf, landmarkName, namePoint, type PointName } from '../lib/placesApi';
import { markBooked, useBack, useEscapeBack } from '../lib/nav';
import { decodePlace } from '../lib/tripUrl';
import { fareFor, getQuote, isFresh } from '../lib/quotes';
import { useRide } from '../lib/ride';
import type { LatLng, Place, Quote } from '../lib/types';
import { words } from '../lib/vehicles';
import { codeOf, friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { useSavePlace } from './placeSave';
import { useColours } from '../theme/usePalette';
import { MapStrip } from './mapBits';

/** The pin's point sits 66 px below the middle of the map (the label stands above it). */
const PIN_DROP = 66;

function LocateButton({ onLocated, onOff }: { onLocated: (at: LatLng) => void; onOff: () => void }) {
  const { locate } = useApp();
  const t = useT();
  const fly = useFlyTo();
  return (
    <button
      type="button"
      aria-label={t('pickup.locate')}
      onClick={async () => {
        const at = await locate();
        if (!at) return onOff();
        fly(at, 17);
        onLocated(at);
      }}
      style={{ position: 'absolute', right: 20, bottom: 'max(72px, calc(var(--map-b) + 28px))', transition: 'bottom .34s var(--ease-out)', width: 44, height: 44, border: 0, borderRadius: 22, background: 'var(--float)', boxShadow: 'var(--shadow)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 6 }}
    >
      <Icon name="locate" size={20} />
    </button>
  );
}

export function ConfirmPickup() {
  const { pick = 'pickup', to } = useSearch({ from: '/pickup' });
  const { api, draft, setDraft, here, locate } = useApp();
  const back = useBack('/choose');
  useEscapeBack(back);
  // opened from a link with the trip in it (and nothing in this tab yet): take the drop from the address
  useEffect(() => {
    const d = decodePlace(to);
    if (d && !draft.drop) setDraft({ drop: d, quote: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  const [info, say] = useNote();
  const t = useT();
  const { setRide } = useRide();
  const nav = useNavigate();
  const pal = useColours('map');
  const { save, sheet, note: saveNote } = useSavePlace();
  const start: Place = pick === 'pickup' ? draft.pickup : pick === 'drop' && draft.drop ? draft.drop : draft.pickup;
  const [center, setCenter] = useState<LatLng>(start);
  const [moving, setMoving] = useState(false);
  const [note, setNote] = useState(draft.note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** the fare the rider last saw (after a re-quote it can change: they confirm the new one) */
  const [agreed, setAgreed] = useState<number | null>(null);

  // where the rider is now, for "1 min walk from you" — only if location is already allowed (never prompts here)
  useEffect(() => {
    if (here || !navigator.permissions?.query) return;
    navigator.permissions.query({ name: 'geolocation' }).then((p) => p.state === 'granted' && void locate(), () => undefined);
  }, [here, locate]);

  // what the pin's spot is called: Ola's address (GET /v1/places/reverse) once the map settles, the nearest
  // landmark until then and whenever search is unavailable
  const [where, setWhere] = useState<PointName>(() => landmarkName(start));
  const asked = useRef(0);
  useEffect(() => {
    const n = ++asked.current;
    setWhere((w) => (w.source === 'landmark' ? landmarkName(center) : w));
    const tm = window.setTimeout(() => {
      void namePoint(api, center).then((name) => n === asked.current && setWhere(name));
    }, 400);
    return () => window.clearTimeout(tm);
  }, [api, center]);
  const spot = useMemo(() => nearestLandmark(center, 250, ['spot']), [center]);
  const walkMin = here ? Math.max(1, Math.round(haversine(here, center) / 80)) : null;
  const booking = pick === 'pickup';
  const fare = fareFor(draft.quote, draft.vehicleType);
  const w = words(t, draft.vehicleType);

  async function book(quote: Quote, place: Place) {
    const opt = quote.options.find((o) => o.vehicleType === draft.vehicleType) ?? quote.options[0];
    // the socket can tell us about the new ride before this call returns: it is ours, on its way to /ride
    markBooked('*');
    const ride = await api
      .book({
        quoteId: quote.quoteId,
        vehicleType: opt.vehicleType,
        pickupAddress: addressOf(where),
        dropAddress: draft.drop?.address ?? undefined,
        pickupNote: note.trim() || undefined,
        rewardId: draft.rewardId ?? undefined,
      })
      .catch((e: unknown) => {
        markBooked(null);
        throw e;
      });
    finishLater.set(null);
    markBooked(ride.id);
    setDraft({ pickup: place, pickupIsHere: draft.pickupIsHere && samePlace(place, draft.pickup, 20), quote: null, note: '', rewardId: null });
    setRide(ride);
    nav({ to: '/ride', replace: true });
  }

  async function confirm() {
    const place: Place = { ...center, address: addressOf(where) };
    if (pick === 'drop') {
      setDraft({ drop: place, quote: null });
      return nav({ to: '/choose' });
    }
    if (pick === 'home' || pick === 'work' || pick === 'place') {
      return save(pick, place, () => nav({ to: pick === 'place' ? '/account/places' : '/home' }));
    }
    if (!draft.drop) return nav({ to: '/search' });
    setBusy(true);
    setError(null);
    let quote = draft.quote;
    try {
      if (!quote || !isFresh(quote) || !samePlace(quote.pickup, center, 20) || !samePlace(quote.drop, draft.drop)) {
        quote = await getQuote(api, place, draft.drop);
        setDraft({ quote });
      }
      const now = fareFor(quote, draft.vehicleType);
      if (agreed != null && now != null && now !== agreed) {
        // the rider confirmed the earlier fare; this one is different — show it and let them confirm again
        setAgreed(now);
        setError(t('pickup.requotedNew', { fare: rupees(now) }));
        return;
      }
      await book(quote, place);
    } catch (e) {
      const code = codeOf(e);
      if (code === 'RIDE_ALREADY_OPEN') return nav({ to: '/ride' });
      if (code === 'QUOTE_EXPIRED' || code === 'QUOTE_INVALID' || code === 'QUOTE_USED') {
        // the signed price ran out (or was used): price the same trip again, fare agreed first
        try {
          const before = fareFor(quote, draft.vehicleType);
          const fresh = await api.quote(place, draft.drop);
          setDraft({ quote: fresh });
          const after = fareFor(fresh, draft.vehicleType);
          if (after != null && after === before) {
            say(t('pickup.requoted', { fare: rupees(after) }), 'good');
            await book(fresh, place);
          } else {
            setAgreed(after);
            setError(t('pickup.requotedNew', { fare: after != null ? rupees(after) : '—' }));
          }
        } catch (e2) {
          setError(friendly(e2, t));
        }
        return;
      }
      if (code === 'REWARD_UNAVAILABLE') setDraft({ rewardId: null });
      setError(friendly(e, t));
    } finally {
      setBusy(false);
    }
  }

  const title = booking ? t('pickup.title') : pick === 'drop' ? t('pickup.setDrop') : pick === 'home' ? t('pickup.setHome') : pick === 'work' ? t('pickup.setWork') : t('pickup.setPlace');
  const button = booking ? t('pickup.confirm') : pick === 'drop' ? t('pickup.dropHere') : t('pickup.savePlace');
  const pinLabel = booking ? t('pickup.pinPickup') : pick === 'drop' ? t('pickup.pinDrop') : t('pickup.pinPlace');

  return (
    <Screen name="confirm-pickup" label={title}>
      <MapView
        label={t('pickup.map', { pin: pinLabel, place: where.title })}
        center={start}
        zoom={17}
        fit={{ points: [start], padding: { top: PIN_DROP * 2, bottom: 0, left: 0, right: 0 }, hold: true }}
        onMoveStart={() => setMoving(true)}
        onMoveEnd={(c) => {
          setMoving(false);
          setCenter(c);
        }}
      >
        {spot && (
          <MapMarker at={spot} anchor="left" z={2}>
            <div style={{ marginLeft: 8, height: 24, padding: '0 8px 0 4px', borderRadius: 8, background: 'var(--float)', boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: 'var(--ink2)', whiteSpace: 'nowrap' }}>
              <Icon name="bank" size={14} sw={2.2} />
              {spot.name}
            </div>
          </MapMarker>
        )}
        {here && haversine(here, center) < 1500 && (
          <>
            <MapLine id="walk" coords={[[here.lng, here.lat], [center.lng, center.lat]]} color={pal.ink} width={2.2} dash={[0.1, 2.4]} />
            <MapMarker at={here} z={3}>
              <span style={{ position: 'relative', display: 'block', width: 44, height: 44, borderRadius: 22, background: 'var(--acSoft)' }}>
                <span style={{ position: 'absolute', left: 14, top: 14, width: 16, height: 16, borderRadius: 8, boxSizing: 'border-box', border: '3px solid #FFFFFF', background: 'var(--you)', boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }} />
              </span>
            </MapMarker>
          </>
        )}
        <LocateButton onLocated={(at) => setCenter(at)} onOff={() => say(t('pickup.locOff'))} />
      </MapView>

      {/* the pin stays in the middle of the free part of the map (above the sheet, right of the panel); the map
          moves under it. When the sheet snaps, the pin glides to the new middle and the map keeps its spot under it. */}
      <div
        role="img"
        aria-label={`${pinLabel}: ${where.title}`}
        style={{ position: 'absolute', left: 'calc(var(--map-l) + (100% - var(--map-l)) / 2)', top: '50%', transform: `translate3d(-50%, calc(var(--map-b) / -2 + ${PIN_DROP - 60}px), 0)`, transition: 'transform .42s var(--ease-out)', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 6, pointerEvents: 'none' }}
      >
        {/* while the map moves the pin lifts and its shadow shrinks; it drops back when the map stops */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', transform: moving ? 'translate3d(0,-10px,0)' : 'none', transition: 'transform .22s var(--ease-out)' }}>
          <div style={{ height: 36, padding: '0 14px', borderRadius: 18, background: 'var(--pri)', color: 'var(--priInk)', display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 6px 16px rgba(0,0,0,0.28)' }}>{pinLabel}</div>
          <div style={{ width: 2.5, height: 21, background: 'var(--pri)' }} />
          <div style={{ width: 12, height: 12, marginTop: -3, borderRadius: 6, background: 'var(--pri)', boxShadow: '0 0 0 3px var(--bg)' }} />
        </div>
        <div aria-hidden="true" style={{ position: 'absolute', bottom: -3, width: 14, height: 5, borderRadius: '50%', background: 'rgba(0,0,0,0.35)', opacity: moving ? 1 : 0, transform: moving ? 'scale(1)' : 'scale(.4)', transition: 'opacity .22s var(--ease), transform .22s var(--ease-out)' }} />
      </div>

      <BackButton floating onClick={back} style={{ position: 'absolute', left: 'calc(var(--map-l) + 20px)', top: 'calc(var(--sat) + 10px)', zIndex: 7 }} />
      {booking && fare != null && (
        <FloatPill style={{ position: 'absolute', right: 20, top: 'calc(var(--sat) + 14px)', padding: '0 14px 0 8px', zIndex: 7 }}>
          <VehicleImage type={draft.vehicleType} width={34} height={20} />
          {w.label} · <span key={fare} className="zr-tick">{rupees(fare)}</span>
        </FloatPill>
      )}

      <MapSheet label={title} art={<MapStrip />}>
        <Handle />
        <h2 style={{ margin: '20px 0 0', fontSize: 22, lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</h2>
        <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)' }}>{t('pickup.drag')}</p>

        <div style={{ marginTop: 16, borderRadius: 18, background: 'var(--s1)', padding: '14px 6px 14px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconTile icon="pin" size={40} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span key={where.title} className="zr-ellipsis zr-tick" style={{ display: 'block', fontSize: 16, lineHeight: '21px', fontWeight: 700 }}>{where.title}</span>
            <span className="zr-ellipsis" style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>
              {where.sub ? `${where.sub} · ` : ''}
              {walkMin != null ? t('pickup.walk', { n: walkMin }) : t('pickup.driverComes')}
            </span>
          </span>
          <button
            type="button"
            onClick={() => nav({ to: '/search', search: { field: booking ? 'pickup' : pick } })}
            style={{ flex: 'none', height: 44, padding: '0 12px', border: 0, background: 'transparent', color: 'var(--acText)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            {t('pickup.edit')}
          </button>
        </div>

        {booking && (
          <label style={{ marginTop: 10, height: 52, borderRadius: 16, background: 'var(--s1)', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink2)', cursor: 'text' }}>
            <Icon name="note" size={20} sw={1.9} />
            <input
              type="text"
              value={note}
              maxLength={140}
              onChange={(e) => (setNote(e.target.value), setDraft({ note: e.target.value }))}
              placeholder={t('pickup.note')}
              aria-label={t('pickup.noteAria')}
              style={{ flex: 1, minWidth: 0, height: 52, border: 0, outline: 0, padding: 0, background: 'transparent', color: 'var(--ink)', fontSize: 16, fontWeight: 500 }}
            />
          </label>
        )}
        {error && (
          <p role="alert" data-testid="pickup-error" key={error} className="zr-note" style={{ fontSize: 14, lineHeight: '20px', fontWeight: 700, color: agreed != null ? 'var(--acText)' : 'var(--danger)' }}>
            <Icon name={agreed != null ? 'info' : 'alert'} size={16} sw={2.2} />
            <span>{error}</span>
          </p>
        )}

        {where.outside && (
          <p role="alert" className="zr-note zr-note--error" data-testid="pickup-outside">
            <Icon name="alert" size={16} sw={2.2} />
            <span>{t('err.OUTSIDE_SERVICE_AREA')}</span>
          </p>
        )}
        <InlineNote note={info} />
        <InlineNote note={saveNote} />
        <SheetFoot style={{ marginTop: 'auto', paddingTop: 'calc(37px * var(--vg))' }}>
          <Button busy={busy} disabled={moving} onClick={() => void confirm()}>
            {button}
          </Button>
        </SheetFoot>
      </MapSheet>
      {sheet}
    </Screen>
  );
}
